import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import { darken, lighten, alpha, duration } from "@mui/material";
import Typography from "@mui/material/Typography";

import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import {   
  // TYPE_NAVIGATION,
  TYPE_CALENDAR,
  // TYPE_AGENDE,
  // TYPE_SCHEDULER,
  TYPE_MODIFIER,

  VIEW_TYPE_YEAR,
  VIEW_TYPE_MONTH,
  VIEW_TYPE_WEEK,
  VIEW_TYPE_DAY,
  VIEW_TYPE_AGENDE,

  VIEW_TYPE_MODIFIER_LAYER0,
  VIEW_TYPE_MODIFIER_LAYER1,

  footerButtons,
  isSelected,

  todayBorder,
} from './Bs4ReportCalendarConst';

import Bs4ReportCalendar from './Bs4ReportCalendar';

import CircularProgress from '@mui/material/CircularProgress';
import * as Bs4Utils from '../../Bs4Components/Bs4Utils'
import { getBoxSx, getBoxStyle, SmallScreenContext, useStateUxTemplate } from '../../Bs4Theme/Bs4UxTemplates';

// import { DndContext, useDroppable, DragOverlay } from '@dnd-kit/core';
// import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { v4 as uuidv4 } from 'uuid';

import {  
  addDays, startOfDay, endOfDay,
  startOfWeek, endOfWeek,  
  startOfMonth,
  compareAsc,
  eachDayOfInterval, eachWeekOfInterval,
  format,
  getWeek, getMonth,  
} from "date-fns";

import { ViewBs4EventEl, ORIENTATION_LIST } from './Bs4ReportCalendarViewBs4Event';

export const RESOURCES_CHANGE = 'resourcesChange';

export default class Bs4ReportCalendarPlanning extends Bs4ReportCalendar {

  constructor(initialValues, viewButtons) {
    super(initialValues, viewButtons)
    this.viewButtons = [];
    this.bs4Calendar.viewType = VIEW_TYPE_MONTH; //this.initialValues.viewType;
  }

  ViewFooter = (props) => {
    const report = this;

    const handleNavigationPreviousPeriod = (e) => {
      this.bs4Calendar.updateRelative(-1);
      this.updateScope();
    }

    const handleNavigationNextPeriod = (e) => {
      this.bs4Calendar.updateRelative(1);
      this.updateScope();
    }        

    return (
      <Stack 
        width='100%'
        direction="row" 
        justifyContent="center" 
        alignItems="center" 
        spacing={1} 
        sx={props.sx}
      >   
        <Stack direction="row" justifyContent="center" alignItems="center" spacing={1}>
          <IconButton onClick={handleNavigationPreviousPeriod}><i className='fa-solid fa-angle-left'></i></IconButton>              
          <Typography>{format(report.bs4Calendar.date, 'LLLL yyyy')}</Typography>
          <IconButton onClick={handleNavigationNextPeriod}><i className='fa-solid fa-angle-right'></i></IconButton>
        </Stack>      
      </Stack>    
    )
  }

  ViewDayName = React.memo((props) => {
    const report = this;
    const formatTxt = props.smallText ? 'iiiii' : 'iii';
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        gridRow={props.gridRow}
        gridColumn={props.gridColumn}
        // sx={{aspectRatio: 1}}
      >
        <Typography variant='subtitle2' sx={{userSelect: 'none', color: '#000000aa'}}>{format(props.day.date, formatTxt)}</Typography>
      </Box> 
    )
  });

  ViewDay = React.memo((props) => {
    const report = this;
    const theme = useTheme();

    const uxDay = getBoxSx(props.uxTemplate?.body?.Bs4ReportCalendarPlanning?.day, theme);

    const [availableColor, availableHoverColor, notAvailableColor, empty, now, selected] = React.useMemo(() => {
      const empty = !(props.day?.bs4Events?.length > 0);
      const now = compareAsc(startOfDay(props.day.date), startOfDay(new Date())) === 0;
      const selected = compareAsc(startOfDay(props.day?.date), startOfDay(props.selected?.date)) === 0;      

      const availableColor = uxDay?.availableColor || lighten(theme.palette.primary.main, 0.4);
      const availableHoverColor = uxDay?.selectedColor || darken(availableColor, 0.3);
      const notAvailableColor = uxDay?.emptyColor || lighten(availableColor, 0.5);
      return [availableColor, availableHoverColor, notAvailableColor, empty, now, selected];
    }, [props.day?.bs4Events?.length, theme, props.calendarChangeTime, props.selected, props.uxDateTimeStamp]);

    const ref = React.useRef(null);    
    const refIb = React.useRef(null);    

    const handleNavigateTo = (event) => {     
      if (!props.day.inMonth) return;
      if (props.onChangeView) props.onChangeView(props.day.date, props.day);
    }    
    
    // console.log('...Bs4ReportCalendarPlanning...ViewDay', availableColor, notAvailableColor)
    
    return (      
      <Box 
        ref={ref}
        gridRow={props.gridRow}
        gridColumn={props.gridColumn}
        display='flex' 
        flexDirection='row' 
        justifyContent='center' 
        alignItems='center'
        sx={{aspectRatio: 1}}
        // border={'solid'}
      >
        <IconButton
          ref={refIb}
          onClick={handleNavigateTo}
          disabled={empty}
          sx={{
            width: '80%',
            height: '80%',          
            border: selected ? `solid ${availableHoverColor}` : ' solid transparent',  
            boxShadow: selected ? uxDay.boxShadow || theme.shadows[7] : theme.shadows[0],            
            outline: now ? todayBorder : null,  
            backgroundColor: empty ? notAvailableColor : availableColor,
            borderRadius: uxDay.borderRadius || '50%',
            borderWidth: uxDay.borderWidth || null,
            '&:hover': {              
              boxShadow: uxDay.boxShadow || theme.shadows[7],
              backgroundColor: availableHoverColor,
            }, 
            '&:disabled': {              
              boxShadow: 'none',
              backgroundColor: notAvailableColor,
            }
          }}
        >
          <Typography 
            variant='subtitle1'
            fontWeight={empty ? 'fontWeightRegular' : 'fontWeightBold'}            
          >
            {format(props.day.date, 'd')}
          </Typography>
        </IconButton>
      </Box>
    )
  });

  ViewMonth = React.memo((props) => {
    const report = this;            
    const refStack = React.useRef(null);

    const [smallText, setSmallText] = React.useState(false);

    React.useEffect(() => {
      if (!refStack.current) return;
      const resizeObserver = new ResizeObserver(() => {
        const rect = refStack.current.getBoundingClientRect();
        setSmallText(Boolean(rect.width < 300));
      });
      resizeObserver.observe(refStack.current);
      return () => resizeObserver.disconnect();
    }, [refStack.current, setSmallText]);
    // console.log('...ViewMonth...', format(props.month?.date, 'LLLL'), props.activeMonth, props.activeMonth.weeks, props.activeMonth.weeks.map);

    return (
      <Stack 
        ref={refStack}
        direction="column" 
        position='relative'
        spacing={0} 
        sx={{
          flex: 1,
          justifyContent: "flex-start", 
          alignItems: "flex-start", 
          //alignItems: 'stretch',
          // padding: 2,
        }}
      >                
        {/* <Typography>{format(props.month?.date, 'LLLL')}</Typography>  */}
        <Box
          display='grid'
          gridTemplateColumns='repeat(7, 1fr)'
          gridTemplateRows='auto repeat(6, 1fr)'
          width={'100%'}
          maxWidth={600}
        >
          {props.month.weeks[0].days.map((day, index) => {
            return (
              <report.ViewDayName 
                key={day.date} 
                day={day} 
                gridRow={'1'}
                gridColumn={`${index + 1}`}
                smallText={smallText}
              />
            )
          })}
          {props.month.weeks.map((week, indexWeek) => {             
            return (
              <React.Fragment key={week.start}>                
                {week.days.map((day, indexDay) => {
                  if (!day.inMonth) return null;
                  return (
                    <report.ViewDay
                      key={day.date}
                      selected={props.selected}
                      day={day}
                      date={props.month.date}
                      onChangeView={props.onChangeView}
                      onRowClick={props.onRowClick}
                      calendarChangeTime={props.calendarChangeTime}
                      gridRow={`${indexWeek + 2}`}
                      gridColumn={`${indexDay + 1}`}
                      uxTemplate={props.uxTemplate}
                      uxDateTimeStamp={props.uxDateTimeStamp}
                    />
                  )
                })}
              </React.Fragment>
            )
          })}                  
        </Box>        
      </Stack>
    );
  });

  ViewComponent = React.forwardRef((props, ref) => {
    const report = this;
    const theme = useTheme();

    const [calendarChangeTime, setCalendarChangeTime] = React.useState(new Date());
    const modifiers = React.useMemo(() => [], [props.viewButtons])
    const [displayWait, setDisplayWait] = React.useState(false);
    const [day, setDay] = React.useState(null);
    const [smallScreen, setSmallScreen] = React.useState(false);
    
    // const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    // const smallScreen = useMediaQuery('@media (max-width:400px)');
    

    const month = React.useMemo(() => {      
      const days = {};
      this.bs4Calendar.getVisibleEvents().forEach(bs4Event => {
        if (compareAsc(bs4Event.end, this.bs4Calendar.start) < 0) return;
        if (compareAsc(bs4Event.start, this.bs4Calendar.end) > 0) return;
        eachDayOfInterval({start: bs4Event.start, end: bs4Event.end}).forEach(date => {
          const key = format(date, 'Y-D', {useAdditionalDayOfYearTokens: true});
          let bs4Events = days[key];
          if (!bs4Events) {
            bs4Events = []
            days[key] = bs4Events;
          }
          bs4Events.push(bs4Event);
        })
      });

      const monthStart = startOfWeek(startOfMonth(this.bs4Calendar.date));
      const monthEnd = addDays(monthStart, 7 * 6 - 1);
      const month = {
        date: this.bs4Calendar.date,
        start: monthStart,
        end: monthEnd,
        weeks: eachWeekOfInterval({start: monthStart, end: monthEnd}).map(weekDate => {
          const weekStart = startOfWeek(weekDate);
          const weekEnd = endOfWeek(weekDate);
          return {
            start: weekStart,
            end: weekEnd,
            days: eachDayOfInterval({start: weekStart, end: weekEnd}).map(dayDate => {
              return {
                date: dayDate,
                bs4Events: days[format(dayDate, 'Y-D', {useAdditionalDayOfYearTokens: true})] || [], 
                inMonth: getMonth(this.bs4Calendar.date) === getMonth(dayDate),
              }
            }),
          }
        }),
      };
      // console.log('...Bs4ReportCalendarPlanning...Mounth...', month)

      const startNow = startOfDay(new Date());
      let okDay = null;
      month.weeks.forEach(week => {
        week.days.forEach(day => {
          if (!day.inMonth) return;
          if (! okDay && day.bs4Events.length > 0) okDay = day;
          if (day.bs4Events.length > 0 && compareAsc(startOfDay(day.date), startNow) === 0 ) okDay = day;
        })
      });

      setDay(okDay);

      return month;
    }, [this.bs4Calendar.date, this.bs4Calendar.start, this.bs4Calendar.end, calendarChangeTime, setDay])

    const elArray = React.useMemo(() => {
      if (!day) return [];
      const elArray = day.bs4Events.map(bs4Event => {
        const bs4EventStart = startOfDay(day.date);
        const bs4EventEnd = endOfDay(day.date);
        return {
          id: uuidv4(),
          bs4Event: bs4Event,
          start: bs4EventStart,
          end: bs4EventEnd,
          startTime: bs4EventStart.getTime(),
          endTime: bs4EventEnd.getTime(),
        }
      })

      return elArray;
    }, [day])

    const handleChangeView = (date, day) => {
      setDay(day)
    }

    React.useEffect(() => {
      this.eventEmit.on(RESOURCES_CHANGE, () => {
        setCalendarChangeTime(new Date());
      });
    }, [setCalendarChangeTime]);

    React.useEffect(() => {
      this.fetchMore = props.fetchMore;
    }, [props.fetchMore]);

    React.useEffect(() => {
      this.updateScope();
      this.setDisplayWait = setDisplayWait;
    }, [this.updateScope, setDisplayWait]);


    const refBox = React.useRef(null);

    React.useEffect(() => {
      if (!refBox.current) return;
      const resizeObserver = new ResizeObserver(() => {
        const rect = refBox.current.getBoundingClientRect();
        setSmallScreen(Boolean(rect.width < 500))
        // console.log('...???...', rect, Boolean(rect.width < 600));
      });
      resizeObserver.observe(refBox.current);
      return () => resizeObserver.disconnect();
    }, [refBox.current, setSmallScreen]);
    
    // console.log('...Bs4ReportCalendarPlanning...View...')

    return (
      <Box
        id='Bs4ReportCalendarPlanning_MainView'
        ref={refBox}
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: smallScreen ? 'column' : 'row',
          gap: 2,
          // justifyContent: "center", 
          // alignItems: "center", 
          overflow: 'hidden',
          // border: 'solid',
        }}
      >                       
        <Box 
          flex={1} 
          display='flex'
          flexDirection='column'
          gap={2}
          // border='solid #00ff00'
        >
          <report.ViewFooter
            uxBodyFooter={props.uxBodyFooter}
          />   
          <report.ViewMonth
            selected={day}
            date={report.bs4Calendar.date} 
            month={month}              
            onChangeView={handleChangeView} 
            onRowClick={props.onRowClick}
            calendarChangeTime={props.calendarChangeTime}
            uxTemplate={props.uxTemplate}
            uxDateTimeStamp={props.uxDateTimeStamp}
          />          
        </Box>
        <Box 
          flex={1} 
          display='flex'
          flexDirection='column'
          gap={2}
          overflow={'hidden'}
        >
          {day?.date ?
          <Stack 
            direction="row" 
            sx={{
              // justifyContent: "center", 
              //alignItems: "center",
              alignItems: "baseline",
            }}
          >
            <Typography>&nbsp;{format(day.date, 'EEEE, dd LLLL yyyy')}</Typography>
            <Typography variant='caption' sx={{opacity: 0.6}}>&nbsp;{'t.'+getWeek(day.date)}</Typography>
          </Stack>
          : null}
          <Stack 
            direction="column" 
            spacing={1} 
            sx={{
              justifyContent: "flex-start", 
              alignItems: "flex-start", 
              overflow: 'auto',
            }}
          >
            {(elArray || []).map((el) => {
              return (              
                <ViewBs4EventEl
                  key={el.bs4Event.id} 
                  orientation={ORIENTATION_LIST}
                  el={el} 
                  uxBodyEvent={props.uxBodyEvent}
                  onRowClick={props.onRowClick}
                  tooltipPlacement='left'
                  sx={{p: 1, cursor: 'pointer'}}
                />        
              )
            })}
          </Stack>
        </Box>
        <Stack
          sx={{
            display: displayWait ? null : 'none', 
            position: 'absolute', 
            top: 0, 
            right: 0, 
            bottom: 0, 
            left: 0,
            backgroundColor: '#00000022',
            zIndex: 100,
          }}
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={2}
        >
          <CircularProgress/>
        </Stack>
      </Box>
    );        
  });
}